/**
 * Analytics helper — wraps window.gtag safely.
 * All events flow through Google Analytics 4 (Measurement ID configured in index.html).
 * Consent Mode v2 controls whether cookies are actually written; events are always sent
 * (as anonymous "consent-denied" pings when consent is not granted).
 */

function gtagSafe(...args) {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  try {
    window.gtag(...args);
  } catch (e) {
    // Never let analytics break the app
    console.warn("gtag call failed:", e?.message);
  }
}

function fbqSafe(...args) {
  if (typeof window === "undefined") return;
  if (typeof window.fbq !== "function") return;
  try {
    window.fbq(...args);
  } catch (e) {
    console.warn("fbq call failed:", e?.message);
  }
}

/** Track a page view manually (useful for SPA route changes). */
export function trackPageView(path) {
  gtagSafe("event", "page_view", {
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : undefined,
  });
  fbqSafe("track", "PageView");
}

/** Fire when the checkout modal opens. */
export function trackBeginCheckout({ productId, productName, price }) {
  gtagSafe("event", "begin_checkout", {
    currency: "EUR",
    value: Number(price) || 0,
    items: [{ item_id: productId, item_name: productName, price: Number(price) || 0, quantity: 1 }],
  });
  fbqSafe("track", "InitiateCheckout", {
    currency: "EUR",
    value: Number(price) || 0,
    content_ids: [productId],
    content_name: productName,
  });
}

/** Fire when Stripe returns a paid session on /checkout/success.
 * eventId (optional) is used for browser Pixel ↔ CAPI deduplication via GTM
 * "Event ID" field mapped to {{dlv - transaction_id}} (defaults to sessionId).
 */
export function trackPurchase({ sessionId, productId, productName, amount, eventId }) {
  gtagSafe("event", "purchase", {
    transaction_id: sessionId,
    event_id: eventId || sessionId,
    currency: "EUR",
    value: Number(amount) || 0,
    items: [{ item_id: productId, item_name: productName, price: Number(amount) || 0, quantity: 1 }],
  });
  fbqSafe("track", "Purchase", {
    currency: "EUR",
    value: Number(amount) || 0,
    content_ids: [productId],
    content_name: productName,
  }, { eventID: eventId || sessionId });
}

/** Fire when the quiz submission returns a recommended course. */
export function trackQuizCompleted({ recommendedCourse, isAllIn, eventId }) {
  gtagSafe("event", "quiz_completed", {
    recommended_course: recommendedCourse,
    is_all_in: !!isAllIn,
    event_id: eventId,
  });
  fbqSafe("track", "Lead", { content_name: recommendedCourse }, eventId ? { eventID: eventId } : undefined);
}

/** Fire when a Cal.com booking is confirmed (from the embed bookingSuccessful event).
 * eventId should be the Cal.com booking uid so browser Pixel and CAPI dedup correctly. */
export function trackSchedule({ eventId, contentName }) {
  gtagSafe("event", "schedule", {
    event_id: eventId,
    content_name: contentName || "orientamento-call",
  });
  fbqSafe(
    "track",
    "Schedule",
    { content_name: contentName || "orientamento-call" },
    eventId ? { eventID: eventId } : undefined
  );
}

/** Fire on landing page mount (view). */
export function trackLandingView({ landingSlug, courseSlug, abVariant }) {
  gtagSafe("event", "landing_view", {
    landing_slug: landingSlug,
    course_slug: courseSlug,
    ab_variant: abVariant,
  });
}

/** Fire when user clicks the primary CTA on a landing page. */
export function trackLandingCtaClick({ landingSlug, courseSlug, ctaLocation, abVariant }) {
  gtagSafe("event", "landing_cta_click", {
    landing_slug: landingSlug,
    course_slug: courseSlug,
    cta_location: ctaLocation, // 'hero' | 'mid' | 'final'
    ab_variant: abVariant,
  });
}

/** Fire when the user opens WhatsApp from the floating button. */
export function trackWhatsAppClick({ context }) {
  gtagSafe("event", "contact_whatsapp", { context: context || "generic" });
}

// ---- Course / Landing conversion tracking (Meta Pixel 1581066943367465) ----

function newEventId(prefix) {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${prefix}-${Date.now()}-${rand}`;
}

const COURSE_TITLES = {
  "controlla-la-tua-comunicazione-digitale": "Controlla la tua Comunicazione Digitale",
  "social-media-marketing": "Social Media Marketing spiegato semplice",
  "facebook-advanced": "Meta Ads Advanced – Campagne professionali",
  "meta-ads-advanced": "Meta Ads Advanced – Campagne professionali",
  "customer-loyalty": "Data-Driven Loyalty & One-Time Offer",
  "agenti-ai": "Agenti AI in Azione",
};
const LANDING_TO_COURSE = {
  cqo: "controlla-la-tua-comunicazione-digitale",
  smm: "social-media-marketing",
  fba: "facebook-advanced",
  cl: "customer-loyalty",
  ai: "agenti-ai",
};

/** Resolve the course display name from a /corso/:slug or /landing-corso-:x path. */
export function courseNameFromPath(pathname = "") {
  let m = pathname.match(/^\/corso\/([^/?#]+)/);
  if (m) return COURSE_TITLES[m[1]] || null;
  m = pathname.match(/^\/landing-corso-([^/?#]+)/);
  if (m) return COURSE_TITLES[LANDING_TO_COURSE[m[1]]] || null;
  return null;
}

/**
 * Fire a Meta Pixel "Lead" on the main enrollment/booking CTA of a course/landing page.
 * Uses a unique eventID (browser-only event, prevents double counting on rapid clicks).
 * Returns the eventId. Fires synchronously, before any navigation.
 */
export function trackCourseLead({ courseName }) {
  const eventId = newEventId("lead-cta");
  gtagSafe("event", "generate_lead", {
    content_name: courseName,
    content_category: "corso",
    value: 260,
    currency: "EUR",
    event_id: eventId,
  });
  fbqSafe(
    "track",
    "Lead",
    { content_name: courseName, content_category: "corso", value: 260, currency: "EUR" },
    { eventID: eventId }
  );
  return eventId;
}

/** Fire a Meta Pixel custom "QuizClick" when the user clicks a quiz link. */
export function trackQuizClick({ courseName } = {}) {
  gtagSafe("event", "quiz_click", { content_name: courseName || undefined });
  fbqSafe("trackCustom", "QuizClick", courseName ? { content_name: courseName } : {});
}

/**
 * Append persisted attribution params (utm_*, fbclid, gclid) from the current URL
 * to a target path, so they survive navigation toward the quiz or the checkout.
 */
export function withTrackingParams(path) {
  if (typeof window === "undefined") return path;
  try {
    const current = new URLSearchParams(window.location.search);
    const keep = new URLSearchParams();
    for (const [k, v] of current.entries()) {
      if (/^utm_/i.test(k) || k === "fbclid" || k === "gclid") keep.set(k, v);
    }
    const qs = keep.toString();
    if (!qs) return path;
    return path + (path.includes("?") ? "&" : "?") + qs;
  } catch {
    return path;
  }
}

/**
 * Update consent when the cookie banner is submitted.
 * status = "granted" | "denied"
 */
export function updateConsent({ analytics, ads }) {
  gtagSafe("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: ads ? "granted" : "denied",
    ad_user_data: ads ? "granted" : "denied",
    ad_personalization: ads ? "granted" : "denied",
  });
  fbqSafe("consent", ads ? "grant" : "revoke");
}
